/**
 * ResourcesWorkspace — Enhanced UI shell for the Resources sub-tab.
 *
 * Replaces direct PreflightChecklist rendering in WorkTabPanel with a
 * consolidated workspace:
 *
 *   1. ReadinessDashboard — weighted scoring summary (always visible when items exist)
 *   2. Unified toolbar    — Cards | List | Dependencies | Print BOM
 *   3. Content area       — PreflightChecklist (cards/list) or ResourceFlowGraph (dependencies)
 *
 * The old summary bar inside PreflightChecklist is hidden via hideSummaryBar prop.
 * View mode is lifted here and passed down via externalViewMode.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import type { PreflightItem, PreflightState, ResourcePhase } from '../../Data/PreFlight/preflightTypes';
import { RESOURCE_PHASES } from '../../Data/PreFlight/preflightTypes';
import PreflightChecklist from '../PreflightChecklist/PreflightChecklist';
import { openBomPrintWindow } from '../../Data/PreFlight/generateBomHtml';
import ReadinessDashboard from './ReadinessDashboard';
import ResourceFlowGraph from './ResourceFlowGraph';
import styles from './ResourcesWorkspace.module.css';

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

export interface ResourcesWorkspaceProps {
  projectGuid: string;
  projectName?: string;
  budgetCurrency?: string;
  items: PreflightItem[];
  onItemsChange: (updatedItems: PreflightItem[]) => void;
  canEdit?: boolean;
  debugMode?: boolean;
}

/* ------------------------------------------------------------------ */
/*  View modes                                                         */
/* ------------------------------------------------------------------ */

type WorkspaceView = 'cards' | 'list' | 'graph';

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function ResourcesWorkspace({
  projectGuid,
  projectName = 'Project',
  budgetCurrency,
  items,
  onItemsChange,
  canEdit = true,
  debugMode,
}: ResourcesWorkspaceProps) {
  const { t } = useAppTranslation('kpi');
  const [activeView, setActiveView] = useState<WorkspaceView>('cards');
  const [showPrintMenu, setShowPrintMenu] = useState(false);
  const printMenuRef = useRef<HTMLDivElement>(null);

  /** Close print menu on outside click. */
  useEffect(() => {
    if (!showPrintMenu) return;
    const handleClick = (e: MouseEvent) => {
      if (printMenuRef.current && !printMenuRef.current.contains(e.target as Node)) {
        setShowPrintMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showPrintMenu]);

  /** Print BOM filtered by phase. null = all phases. */
  const handlePrintBom = useCallback((phase: ResourcePhase | null) => {
    const filtered = phase ? items.filter((i) => (i.phase ?? 'preparation') === phase) : items;
    openBomPrintWindow(filtered, projectName, budgetCurrency);
    setShowPrintMenu(false);
  }, [items, projectName, budgetCurrency]);

  /** Filter state click from dashboard — switch to list view. */
  const handleFilterByState = useCallback((_state: PreflightState) => {
    setActiveView('list');
  }, []);

  /** Graph node click → switch to list view to edit. */
  const handleGraphSelectItem = useCallback((_item: PreflightItem) => {
    setActiveView('list');
  }, []);

  /** Derive the checklist view mode from the workspace view. */
  const checklistViewMode: 'cards' | 'list' =
    activeView === 'graph' ? 'list' : activeView;

  return (
    <div className={styles.workspace}>
      {/* ── Dashboard (always visible when items exist) ──────────── */}
      {items.length > 0 && (
        <ReadinessDashboard
          items={items}
          onFilterByState={handleFilterByState}
        />
      )}

      {/* ── Unified toolbar ──────────────────────────────────────── */}
      {items.length > 0 && (
        <div className={styles.viewBar}>
          <div className={styles.viewToggle}>
            <button
              type="button"
              className={activeView === 'cards' ? styles.viewBtnActive : styles.viewBtn}
              onClick={() => setActiveView('cards')}
              aria-label={t('preflight.viewCards')}
              title={t('preflight.viewCards')}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="7" height="7" rx="1" />
                <rect x="14" y="3" width="7" height="7" rx="1" />
                <rect x="3" y="14" width="7" height="7" rx="1" />
                <rect x="14" y="14" width="7" height="7" rx="1" />
              </svg>
              {t('preflight.viewCards')}
            </button>
            <button
              type="button"
              className={activeView === 'list' ? styles.viewBtnActive : styles.viewBtn}
              onClick={() => setActiveView('list')}
              aria-label={t('preflight.viewList')}
              title={t('preflight.viewList')}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="8" y1="6" x2="21" y2="6" />
                <line x1="8" y1="12" x2="21" y2="12" />
                <line x1="8" y1="18" x2="21" y2="18" />
                <line x1="3" y1="6" x2="3.01" y2="6" />
                <line x1="3" y1="12" x2="3.01" y2="12" />
                <line x1="3" y1="18" x2="3.01" y2="18" />
              </svg>
              {t('preflight.viewList')}
            </button>
            <button
              type="button"
              className={activeView === 'graph' ? styles.viewBtnActive : styles.viewBtn}
              onClick={() => setActiveView('graph')}
              aria-label={t('preflight.viewGraph')}
              title={t('preflight.viewGraph')}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="6" cy="6" r="3" />
                <circle cx="18" cy="6" r="3" />
                <circle cx="12" cy="18" r="3" />
                <line x1="8.5" y1="7.5" x2="10.5" y2="16" />
                <line x1="15.5" y1="7.5" x2="13.5" y2="16" />
              </svg>
              {t('preflight.viewGraph')}
            </button>
          </div>

          {/* BOM Print with phase filter */}
          <div className={styles.printWrapper} ref={printMenuRef}>
            <button
              type="button"
              className={styles.toolbarBtn}
              onClick={() => setShowPrintMenu((prev) => !prev)}
              aria-label={t('preflight.bomPrint')}
              title={t('preflight.bomPrint')}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="6 9 6 2 18 2 18 9" />
                <path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2" />
                <rect x="6" y="14" width="12" height="8" />
              </svg>
              {t('preflight.bomPrint')}
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>

            {showPrintMenu && (
              <div className={styles.printMenu}>
                <button
                  type="button"
                  className={styles.printMenuItem}
                  onClick={() => handlePrintBom(null)}
                >
                  {t('preflight.bomAllPhases')}
                </button>
                {RESOURCE_PHASES.map((phase) => {
                  const phaseCount = items.filter((i) => (i.phase ?? 'preparation') === phase).length;
                  if (phaseCount === 0) return null;
                  return (
                    <button
                      key={phase}
                      type="button"
                      className={styles.printMenuItem}
                      onClick={() => handlePrintBom(phase)}
                    >
                      {t(`preflight.phases.${phase}`)}
                      <span className={styles.printMenuCount}>{phaseCount}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Content area ─────────────────────────────────────────── */}
      {activeView !== 'graph' && (
        <PreflightChecklist
          projectGuid={projectGuid}
          projectName={projectName}
          budgetCurrency={budgetCurrency}
          items={items}
          onItemsChange={onItemsChange}
          canEdit={canEdit}
          debugMode={debugMode}
          hideSummaryBar
          externalViewMode={checklistViewMode}
        />
      )}

      {activeView === 'graph' && items.length > 0 && (
        <ResourceFlowGraph
          items={items}
          onSelectItem={handleGraphSelectItem}
        />
      )}
    </div>
  );
}
